import type { EdgeChartApi } from '@edge/charts'
import type { ChartDrawingsStore } from '@/features/charts/chart-assets-store'

/**
 * Registry of live chart APIs keyed by panel, so a non-chart panel (the Trade
 * Planner's Send levels to chart) can reach a chart it is linked to. There is
 * no other seam: the imperative API is only handed out through `chartApiRef`
 * on each chart cell. Cells register on mount and unregister on unmount.
 */
const apis = new Map<string, Map<string, EdgeChartApi>>()

export function registerChartApi(panelId: string, cellId: string, api: EdgeChartApi | null): void {
  let cells = apis.get(panelId)
  if (api) {
    if (!cells) {
      cells = new Map()
      apis.set(panelId, cells)
    }
    cells.set(cellId, api)
  } else if (cells) {
    cells.delete(cellId)
    if (cells.size === 0) apis.delete(panelId)
  }
}

/** The first live chart API of a panel (its primary cell), or null. */
export function getChartApi(panelId: string): EdgeChartApi | null {
  const cells = apis.get(panelId)
  if (!cells || cells.size === 0) return null
  return cells.get(panelId) ?? cells.values().next().value ?? null
}

export function chartPanelIds(): string[] {
  return Array.from(apis.keys())
}

/** One cell's live chart API: the panel itself when 1×1, `panelId::i` in a grid. */
export function getChartCellApi(panelId: string, cellId: string): EdgeChartApi | null {
  return apis.get(panelId)?.get(cellId) ?? null
}

/** The cells a panel has registered, in cell order. */
export function chartCellIds(panelId: string): string[] {
  return Array.from(apis.get(panelId)?.keys() ?? []).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  )
}

// Where a chart panel's own header controls act: the panel when 1×1, else the
// grid's active cell, with the timeframe the panel's while interval sync is on.
// Code outside the chart (the companion's set_timeframe and configure_indicator)
// writes where the header would; a grid cell reads its own state, so a write to
// the panel alone changed nothing on screen.
export interface ChartControlTarget {
  /** The cell the header acts on: the panel id when 1×1, else `panelId::i`. */
  activeCellId: string
  /** Interval sync on: the timeframe is the panel's, and every cell follows it. */
  intervalSynced: boolean
}

const controlTargets = new Map<string, () => ChartControlTarget>()

export function registerChartControlTarget(
  panelId: string,
  read: (() => ChartControlTarget) | null,
): void {
  if (read) controlTargets.set(panelId, read)
  else controlTargets.delete(panelId)
}

export function getChartControlTarget(panelId: string): ChartControlTarget | null {
  return controlTargets.get(panelId)?.() ?? null
}

// A chart panel's ticker belongs to the workspace (symbol list + the panel's
// symbolIndex + link-group broadcast), not to the chart. A panel that wants
// another chart on a ticker goes through the setter that chart registered
// (the same path as typing the ticker into its header), never `api.setSymbol`.
const tickerSetters = new Map<string, (ticker: string) => void>()

export function registerChartTickerSetter(
  panelId: string,
  setter: ((ticker: string) => void) | null,
): void {
  if (setter) tickerSetters.set(panelId, setter)
  else tickerSetters.delete(panelId)
}

export function getChartTickerSetter(panelId: string): ((ticker: string) => void) | null {
  return tickerSetters.get(panelId) ?? null
}

// A chart panel's drawings live in its page's drawings store (per ticker), not
// in the chart. Code outside the chart (the companion's AI drawings) writes
// there, so every chart on the ticker repaints and Undo covers it.
const drawingsStores = new Map<string, ChartDrawingsStore>()

export function registerChartDrawingsStore(
  panelId: string,
  store: ChartDrawingsStore | null,
): void {
  if (store) drawingsStores.set(panelId, store)
  else drawingsStores.delete(panelId)
}

export function getChartDrawingsStore(panelId: string): ChartDrawingsStore | null {
  return drawingsStores.get(panelId) ?? null
}

/** Test helper. */
export function resetChartApiRegistry(): void {
  apis.clear()
  tickerSetters.clear()
  drawingsStores.clear()
  controlTargets.clear()
}
